import { useSearchPermissionGroups } from "@entities/policies/lib/hooks/useSearchPermissionGroups";
import ManagePoliciesDrawer from "@entities/policies/ui/ManagePoliciesDrawer";
import { countryCodeOptions } from "@shared/lib/constants";
import { ToastService } from "@shared/lib/services";
import { IGetOrganisationPermissions } from "@shared/lib/types";
import { Button, Col, Flex, Form, Input, Row, Select, Switch } from "antd";
import { useEffect, useState } from "react";
import { useCreateOrganisationUser } from "../lib/hooks/useCreateOrganisationUser";
import { useEditOrganisationUser } from "../lib/hooks/useEditOrganisationUser";
import useGetOrganisationById from "../lib/hooks/useGetOrganisationById";

const { Option } = Select;

export default function OrganisationUserForm({
  userPermissions,
  organisationId,
  isEdit,
  onCancel,
}: {
  userPermissions: IGetOrganisationPermissions | null;
  isEdit?: boolean;
  onCancel: () => void;
  organisationId: string;
}) {
  const {
    createOrganisationUser,
    isCreatingOrganisationUser,
    isCreatedOrganisationUser,
  } = useCreateOrganisationUser();

  const {
    updateOrganisationUser,
    isUpdatingOrganisationUser,
    isUpdatedOrganisationUser,
  } = useEditOrganisationUser(organisationId);
  const {
    permissionGroups,
    isFetchingPermissionGroups: isPermissionGroupsFetching,
  } = useSearchPermissionGroups({
    filterBy: {
      brandOrganisationId: organisationId,
    },
    limit: 100,
    pageNo: 1,
  });
  const [openPoliciesDrawer, setOpenPoliciesDrawer] = useState<boolean>(false);
  const { organisation, isLoading } = useGetOrganisationById(organisationId);
  const [form] = Form.useForm();

  useEffect(() => {
    if (isCreatedOrganisationUser || isUpdatedOrganisationUser) {
      ToastService.success(
        "Employee " + (isEdit ? "updated" : "created") + " successfully"
      );
      onCancel();
    }
  }, [isCreatedOrganisationUser, isUpdatedOrganisationUser]);

  return (
    <Form
      form={form}
      layout="vertical"
      initialValues={
        isEdit
          ? {
              firstName: userPermissions?.user?.firstName,
              lastName: userPermissions?.user?.lastName,
              emailId: userPermissions?.user?.emailId,
              phoneNumber: userPermissions?.user?.phoneNumber,
              role: userPermissions?.user?.role,
              brandIds: userPermissions?.brandIds,
              countryCode: userPermissions?.user?.countryCode,
              permissionGroupIds: userPermissions?.permissions,
              isTestUser: userPermissions?.user?.isTestUser || false,
            }
          : {}
      }
      onFinish={(values) => {
        console.log("values", values);
        if (isEdit) {
          updateOrganisationUser({
            brandUser: {
              ...values,
              permissionGroupIds:
                typeof values.permissionGroupIds === "string"
                  ? [values.permissionGroupIds]
                  : values.permissionGroupIds,
            },
            brandUserId: userPermissions?.userId || "",
            // brandOrganisationId: organisationId,
          });
        } else {
          createOrganisationUser({
            brandUser: {
              ...values,
              permissionGroupIds:
                typeof values.permissionGroupIds === "string"
                  ? [values.permissionGroupIds]
                  : values.permissionGroupIds,
            },
            brandOrganisationId: organisationId,
          });
        }
      }}
    >
      <Row gutter={16}>
        <Col span={12}>
          <Form.Item
            label="First Name"
            name="firstName"
            rules={[{ required: true, message: "First name is required" }]}
          >
            <Input placeholder="Enter first name" />
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item
            label="Last Name"
            name="lastName"
            rules={[{ required: true, message: "Last name is required" }]}
          >
            <Input placeholder="Enter last name" />
          </Form.Item>
        </Col>
      </Row>
      <Form.Item
        label="Email"
        name="emailId"
        rules={[
          {
            required: true,
            type: "email",
            message: "A valid email is required",
          },
        ]}
      >
        <Input type="email" placeholder="Enter email" />
      </Form.Item>
      <Form.Item label="Phone Number">
        <Input.Group compact>
          <Form.Item
            name="countryCode"
            noStyle
            rules={[{ required: true, message: "Country code is required" }]}
          >
            <Select style={{ width: "30%" }} placeholder="Country Code">
              {countryCodeOptions.map((option) => (
                <Option key={option.value} value={option.value}>
                  {option.label}
                </Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item
            name="phoneNumber"
            noStyle
            rules={[
              { required: true, message: "Phone number is required" },
              { pattern: /^\d+$/, message: "Phone number must be numeric" },
            ]}
          >
            <Input style={{ width: "70%" }} placeholder="Phone Number" />
          </Form.Item>
        </Input.Group>
      </Form.Item>
      <Form.Item label="Designation" name="role">
        <Input placeholder="Enter designation" />
      </Form.Item>
      <Form.Item
        name="brandIds"
        rules={[
          {
            required: true,
            message: "Please select a brand.",
          },
        ]}
        label={
          <Flex
            justify="space-between"
            align="center"
            style={{ width: "100%" }}
          >
            Brands
            <Button
              type="link"
              onClick={() => {
                form.setFieldValue(
                  "brandIds",
                  organisation?.brands?.map((brand) => brand._id)
                );
              }}
            >
              Select All
            </Button>
          </Flex>
        }
      >
        <Select
          mode="multiple"
          options={organisation?.brands?.map((brand) => ({
            label: brand.name,
            value: brand._id,
          }))}
          placeholder="Select brands"
          maxTagCount={3}
        />
      </Form.Item>
      <Form.Item
        name="permissionGroupIds"
        label={
          <Flex align="center" style={{ width: "100%" }}>
            <span>Permission Group</span>
            <Button type="link" onClick={() => setOpenPoliciesDrawer(true)}>
              Manage Permissions
            </Button>
          </Flex>
        }
        rules={[
          {
            required: true,
            message: "Please select a permission group.",
          },
        ]}
      >
        <Select
          placeholder="Select Permission Group"
          options={permissionGroups?.data.data?.map((group) => ({
            label: group.name,
            value: group._id,
          }))}
        />
      </Form.Item>
      <Form.Item name="isTestUser" label="Is Test User?">
        <Switch />
      </Form.Item>
      <Form.Item>
        <Flex justify="flex-end" gap={10}>
          <Button onClick={onCancel}>Cancel</Button>
          <Button
            type="primary"
            htmlType="submit"
            loading={isUpdatingOrganisationUser || isCreatingOrganisationUser}
          >
            {isEdit ? "Update Employee" : "Add Employee"}
          </Button>
        </Flex>
      </Form.Item>
      {openPoliciesDrawer ? (
        <ManagePoliciesDrawer
          isOpen={openPoliciesDrawer}
          onClose={() => {
            setOpenPoliciesDrawer(false);
          }}
          brandOrganisationId={organisationId}
        />
      ) : null}
    </Form>
  );
}
